import React from "react";
import { Box, Typography, Tooltip, Chip } from "@mui/material";
import {
  Email as EmailIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import type {
  ShipmentEventApi,
  DownloadAttachmentRequest,
  DownloadAttachmentResponse,
  EmailCategory,
} from "../../../src/shipments/types";
import { apiClient } from "../utils/apiClient";
import { EventLogger } from "@/utils/eventLogger";
import { useSelector } from "react-redux";
import type { AppState } from "../store";

interface TimelineEventProps {
  event: ShipmentEventApi;
  onOpenEmail: (emailId: string, eventName: string) => void;
  isLast: boolean;
}

// Helper function to render email addresses with overflow handling
const renderEmailAddresses = (addresses: string[], label: string) => {
  if (!addresses || addresses.length === 0) return null;

  if (addresses.length === 1) {
    return (
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.7rem",
          color: "text.disabled",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <strong>{label}:</strong> {addresses[0]}
      </Typography>
    );
  }

  const firstAddress = addresses[0];
  const remainingCount = addresses.length - 1;
  const allAddresses = addresses.join(", ");

  return (
    <Typography
      variant="caption"
      sx={{
        fontSize: "0.7rem",
        color: "text.disabled",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <strong>{label}:</strong> {firstAddress}{" "}
      <Tooltip title={allAddresses} arrow>
        <span style={{ textDecoration: "underline", cursor: "pointer" }}>
          (+{remainingCount} other{remainingCount > 1 ? "s" : ""})
        </span>
      </Tooltip>
    </Typography>
  );
};

// Helper function to format category names for display
const formatCategory = (category: string): string => {
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const ShipmentTimelineEvent: React.FC<TimelineEventProps> = ({
  event,
  onOpenEmail,
  isLast,
}) => {
  const eventDate = event.date ? new Date(event.date) : null;
  const isAdmin =
    useSelector((state: AppState) => state.userData.user?.isAdmin) || false;

  const handleDownloadAttachment = async (
    emailId: string,
    attachmentId: string,
    filename?: string
  ) => {
    try {
      const response = await apiClient.post<
        DownloadAttachmentResponse,
        DownloadAttachmentRequest
      >("/shipments/download-attachment", { emailId, attachmentId });

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = response.downloadUrl;
      link.download = response.filename || filename || "attachment";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      EventLogger.getInstance().track({
        name: "click",
        action: "download_attachment",
        attachmentName: response.filename || filename || "<unknown>",
      });
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        pl: "50px",
        pb: isLast ? 0 : 3,
        minHeight: isLast ? "auto" : 80,
        "&:hover .email-icon": {
          opacity: 1,
        },
      }}
    >
      {/* Date on the left side of circle */}
      <Tooltip
        title={eventDate ? format(eventDate, "MMM d, yyyy 'at' h:mm a") : ""}
        placement="left"
      >
        <Typography
          variant="caption"
          color="primary.main"
          sx={{
            position: "absolute",
            left: -20,
            top: 6,
            fontSize: "0.65rem",
            fontWeight: 500,
            textAlign: "right",
            width: 40,
          }}
        >
          {eventDate ? format(eventDate, "MMM d") : "-"}
        </Typography>
      </Tooltip>

      {/* Timeline circle */}
      <Box
        sx={{
          position: "absolute",
          left: 26,
          top: 4,
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: "primary.main",
          zIndex: 1,
          border: "3px solid white",
          boxShadow: 1,
        }}
      />

      {/* Main content container with two columns */}
      <Box sx={{ pt: 0.5, display: "flex", gap: 2 }}>
        {/* Left column: Event details */}
        <Box
          className="LeftColumnDetails"
          sx={{
            position: "relative",
            flex: 1,
            minWidth: 0, // Allows the box to shrink below its content size
            maxWidth: "calc(100% - 250px - 16px)", // Ensures it doesn't push the attachments column
            overflow: "hidden", // Prevents content from overflowing
            "&:hover .open-email-chip": {
              opacity: 1,
              visibility: "visible",
            },
          }}
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            color="primary"
            sx={{
              mb: 0.5,
              // overflow: "hidden",
              textOverflow: "ellipsis",
              // whiteSpace: "nowrap",
            }}
          >
            {event.name}
          </Typography>

          {/* From/To email addresses - only show when we have actual data */}
          {event.emailFrom && event.emailFrom.length > 0 && (
            <Box sx={{ mb: 0 }}>
              {renderEmailAddresses(event.emailFrom, "From")}
            </Box>
          )}

          {event.emailTo && event.emailTo.length > 0 && (
            <Box sx={{ mb: 0.5 }}>
              {renderEmailAddresses(event.emailTo, "To")}
            </Box>
          )}

          {/* Description */}
          {event.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {event.description}
            </Typography>
          )}

          {/* Email subject line with icon - always visible when email exists */}
          {event.emailId && event.emailSubject && (
            <Box
              onClick={() => onOpenEmail(event.emailId!, event.name)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                cursor: "pointer",
                "&:hover .email-subject": {
                  textDecoration: "underline",
                },
                "&:hover .email-icon-inline": {
                  color: "primary.main",
                },
              }}
            >
              <EmailIcon
                className="email-icon-inline"
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  transition: "color 0.2s ease",
                }}
              />
              <Typography
                className="email-subject"
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: "text.disabled",
                  transition: "text-decoration 0.2s ease",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {event.emailSubject}
              </Typography>
            </Box>
          )}

          {/* Categories - only visible for admin users */}
          {isAdmin && event.categories && event.categories.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {event.categories.map((category) => (
                <Chip
                  key={category}
                  label={formatCategory(category)}
                  size="small"
                  sx={{
                    height: "18px",
                    fontSize: "9px",
                    backgroundColor: "primary.50",
                    color: "primary.main",
                    fontWeight: 500,
                    "& .MuiChip-label": {
                      padding: "0 6px",
                    },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Hover chip for opening email - only show if email exists */}
          {event.emailId && (
            <Chip
              className="open-email-chip"
              label="open email"
              icon={
                <EmailIcon
                  sx={{ fontSize: "14px !important", color: "white" }}
                />
              }
              size="small"
              clickable
              onClick={() => onOpenEmail(event.emailId!, event.name)}
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                opacity: 0,
                visibility: "hidden",
                transition: "all 0.2s ease",
                fontSize: "0.7rem",
                height: "24px",
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
                "& .MuiChip-label": {
                  fontSize: "0.7rem",
                  padding: "0 8px",
                },
                "& .MuiChip-icon": {
                  marginLeft: "6px",
                  marginRight: "0px",
                  color: "white",
                },
              }}
            />
          )}
        </Box>

        {/* Right column: Attachments - Always takes 250px space */}
        <Box sx={{ width: 250, flexShrink: 0 }}>
          {/* Attachments or empty space */}
          {event.attachments &&
          event.attachments.length > 0 &&
          event.emailId ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {event.attachments.map((attachment) => (
                <Box
                  key={attachment.emailAttachmentId}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "4px",
                    p: 1,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "primary.50",
                    },
                  }}
                  onClick={() =>
                    handleDownloadAttachment(
                      event.emailId!,
                      attachment.emailAttachmentId,
                      attachment.filename
                    )
                  }
                >
                  {/* Description above attachment */}
                  {attachment.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "10px",
                        color: "text.secondary",
                        lineHeight: 1.2,
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      {attachment.description}
                    </Typography>
                  )}

                  {/* Attachment display */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DownloadIcon
                      sx={{ fontSize: "12px", color: "primary.main" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        width: "calc(100% - 30px)",
                        wordBreak: "break-word",
                        fontSize: "10px",
                        fontWeight: "bold",
                        lineHeight: 1,
                        color: "primary.main",
                      }}
                    >
                      {attachment.filename || "Download"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            // Empty space when no attachments
            <Box sx={{ minHeight: 40 }} />
          )}
        </Box>
      </Box>
    </Box>
  );
};
